"use client";

import { useEffect } from "react";
import { useTranslations } from "@/lib/compat/i18n";
import clsx from "clsx";

/**
 * Split-screen handshake renderer. Per design principle 9:
 *   "Five and only five split-screen handshakes (#1–#5)."
 *
 * Used by Acts 0.6 / 1.6 / 2.1 / 2.7 / 3.4. Each handshake marks a moment
 * where both parties' state changes simultaneously: gate, mint, contract,
 * customs, LC.
 *
 * Pane labels use start/end semantics rather than left/right — under RTL
 * the "Start pane" auto-flips to the right edge, "End pane" to the left,
 * matching the reading direction. The labels themselves are translated.
 *
 * While mounted, sets html[data-handshake="active"] so AgentLayer and
 * IdleHint suppress themselves via CSS (see globals.css). Bubbles colliding
 * with the "Handshake #N" ribbon at the same coords would dilute the moment.
 */
export function SplitScreen({
  number,
  caption,
  left,
  right,
  className,
}: {
  number: number;
  caption: string;
  left: React.ReactNode;
  right: React.ReactNode;
  className?: string;
}) {
  const t = useTranslations("splitScreen");

  useEffect(() => {
    document.documentElement.dataset.handshake = "active";
    return () => { delete document.documentElement.dataset.handshake; };
  }, []);

  return (
    <div className={clsx("relative grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-state-verified/30 bg-state-verified/30", className)}>
      <div className="relative bg-orbitra-ink/85 p-4">
        <span className="absolute start-3 top-3 font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("startPane")}</span>
        {left}
      </div>
      <div className="relative bg-orbitra-ink/85 p-4">
        <span className="absolute start-3 top-3 font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("endPane")}</span>
        {right}
      </div>
      <div className="absolute start-1/2 top-3 z-10 -translate-x-1/2 rounded-full bg-state-verified px-3 py-1 font-mono text-[10px] font-semibold text-orbitra-ink shadow-[0_0_24px_var(--state-verified-glow)]">
        {t("handshake")} #{number}
      </div>
      <p className="col-span-2 bg-orbitra-ink/95 px-4 py-2 text-[11px] text-orbitra-teal-soft">{caption}</p>
    </div>
  );
}
